// ================================
// DRIVER QUEUE - PHASE 2
// Need Help button, Attempted Delivery, earnings display
// ================================
import React, { useEffect, useState, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useSocket } from '../../context/SocketContext';
import axios from 'axios';
import { Html5Qrcode } from 'html5-qrcode';
import ChatThread from '../../components/shared/ChatThread';
import InstallPrompt from '../../components/shared/InstallPrompt';
import DeliveryMiniMap from '../../components/shared/DeliveryMiniMap';
import useTelegramConnect from '../../utils/useTelegramConnect';

function getCurrentLocation() {
  return new Promise((resolve) => {
    if (!navigator.geolocation) return resolve(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => resolve(null),
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 0 }
    );
  });
}

export default function DriverQueue() {
  const { user, logout }        = useAuth();
  const socket                  = useSocket();
  const [orders, setOrders]     = useState([]);
  const [history, setHistory]   = useState([]);
  const [sessionDeliveries, setSessionDeliveries] = useState([]); // this app session only, for the "Today" stat tile
  const [tab, setTab]           = useState('queue');
  const [loading, setLoading]   = useState(true);
  const [delivering, setDelivering] = useState(null);
  const [attempting, setAttempting] = useState(null);
  const [timers, setTimers]     = useState({}); // { oid: secondsElapsed }
  const [earnings, setEarnings] = useState({ today: 0, week: 0, balance: 0 });
  const [showScanner, setShowScanner] = useState(false);
  const [scanBusy, setScanBusy] = useState(false);
  const [showCodeEntry, setShowCodeEntry] = useState(false);
  const [codeInput, setCodeInput] = useState('');
  const [codeBusy, setCodeBusy] = useState(false);
  const [chatOid, setChatOid]   = useState(null);
  const [showDispatchChat, setShowDispatchChat] = useState(false);
  const [myLocation, setMyLocation] = useState(null); // {lat,lng} — for the embedded mini map only, separate from the socket ping below
  // The mini map shows by default for every active order (no tap needed) —
  // this tracks which ones the driver has explicitly collapsed, not which
  // ones are shown.
  const [hiddenMaps, setHiddenMaps] = useState(() => new Set());
  const toggleMap = (oid) => setHiddenMaps(prev => {
    const next = new Set(prev);
    if (next.has(oid)) next.delete(oid); else next.add(oid);
    return next;
  });
  const [navPickerOid, setNavPickerOid] = useState(null); // which order's "choose nav app" menu is open, if any
  const [unread, setUnread] = useState({ directThread: null, orderThreads: {}, totalUnread: 0 });
  const [promos, setPromos] = useState([]); // live promos admin has posted for drivers
  const [promoProgress, setPromoProgress] = useState([]); // this driver's own progress toward any live delivery-count goal
  const timerRefs               = useRef({});
  const scannerRef              = useRef(null);
  const telegram = useTelegramConnect('driver');

  useEffect(() => {
    fetchOrders();
    fetchEarnings();
    fetchHistory();
    fetchUnread();
    fetchPromos();
    fetchPromoProgress();
    // Fallback polling in case a socket push is missed (phone backgrounded, brief disconnect, etc.)
    const poll = setInterval(() => { fetchOrders(); fetchEarnings(); fetchUnread(); }, 15000);
    return () => clearInterval(poll);
  }, []);

  useEffect(() => {
    if (!socket) return;
    socket.on('order:assigned', () => fetchOrders());
    socket.on('order:removed',  (data) => setOrders(prev => prev.filter(o => o.oid !== data.oid)));
    socket.on('order:updated',  () => fetchOrders());
    socket.on('message:new',    () => fetchUnread());
    socket.on('earnings:updated', ({ earned, promoTitle }) => {
      fetchEarnings(); fetchPromoProgress();
      if (promoTitle) alert(`🎉 Promo goal hit — "${promoTitle}" — +${earned} coins credited!`);
    });
    socket.on('promos:updated', () => { fetchPromos(); fetchPromoProgress(); });
    socket.on('connect',        () => { fetchOrders(); fetchEarnings(); fetchUnread(); }); // catch up on reconnect
    return () => { socket.off('order:assigned'); socket.off('order:removed'); socket.off('order:updated'); socket.off('message:new'); socket.off('earnings:updated'); socket.off('promos:updated'); socket.off('connect'); };
  }, [socket]);

  // While there's at least one active delivery, periodically share location
  // so customers get a live ETA. Silently does nothing if location is denied
  // or unavailable — ETA just won't show for them.
  useEffect(() => {
    if (!socket || orders.length === 0) return;
    function ping() {
      if (!navigator.geolocation) return;
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const loc = { lat: pos.coords.latitude, lng: pos.coords.longitude };
          socket.emit('driver:location', { driverName: user?.name, ...loc });
          setMyLocation(loc); // also keep locally for the embedded mini map
        },
        () => {},
        { enableHighAccuracy: false, timeout: 8000, maximumAge: 15000 }
      );
    }
    ping();
    const interval = setInterval(ping, 25000);
    return () => clearInterval(interval);
  }, [socket, orders.length, user]);

  async function fetchOrders() {
    try {
      const res = await axios.get('/api/orders/driver');
      setOrders(res.data.orders || []);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  }

  async function fetchEarnings() {
    try {
      const res = await axios.get('/api/drivers/earnings');
      setEarnings(res.data || { today: 0, week: 0, balance: 0 });
    } catch(e) { console.error(e); }
  }

  async function fetchUnread() {
    try {
      const res = await axios.get('/api/messages/my-unread');
      setUnread(res.data);
    } catch(e) {}
  }

  async function fetchHistory() {
    try {
      const res = await axios.get('/api/orders/history');
      setHistory(res.data.orders || []);
    } catch(e) { console.error(e); }
  }

  async function fetchPromos() {
    try {
      const res = await axios.get('/api/promos/live');
      setPromos(res.data.promos || []);
    } catch(e) {}
  }

  async function fetchPromoProgress() {
    try {
      const res = await axios.get('/api/promos/my-progress');
      setPromoProgress(res.data.progress || []);
    } catch(e) {}
  }

  // Start timer when driver marks "Driver Arrived"
  function startTimer(oid) {
    if (timerRefs.current[oid]) return;
    timerRefs.current[oid] = setInterval(() => {
      setTimers(prev => ({ ...prev, [oid]: (prev[oid] || 0) + 1 }));
    }, 1000);
  }

  function stopTimer(oid) {
    if (timerRefs.current[oid]) {
      clearInterval(timerRefs.current[oid]);
      delete timerRefs.current[oid];
    }
    setTimers(prev => { const next = {...prev}; delete next[oid]; return next; });
  }

  async function markArrived(oid) {
    try {
      await axios.put(`/api/orders/${oid}/status`, { status: 'Driver Arrived' });
      startTimer(oid);
      fetchOrders();
    } catch(e) { alert('Failed to update status'); }
  }

  async function markDelivered(oid) {
    setDelivering(oid);
    try {
      const loc = await getCurrentLocation();
      await axios.put(`/api/orders/${oid}/deliver`, loc || {});
      stopTimer(oid);
      const delivered = orders.find(o => o.oid === oid);
      if (delivered) setSessionDeliveries(prev => [delivered, ...prev]);
      setOrders(prev => prev.filter(o => o.oid !== oid));
      fetchEarnings();
      fetchHistory();
    } catch(e) { alert(e.response?.data?.error || 'Failed'); }
    finally { setDelivering(null); }
  }

  // Start/stop the camera scanner while the modal is open
  useEffect(() => {
    if (!showScanner) return;
    const scanner = new Html5Qrcode('qr-reader');
    scannerRef.current = scanner;
    let stopped = false;
    // start() can fail (permission denied, no camera) — stop() on a
    // scanner that never actually got running throws synchronously, not
    // just a rejected promise, so cleanup has to know whether it's safe
    // to call at all.
    let started = false;

    scanner.start(
      { facingMode: 'environment' },
      { fps: 10, qrbox: 220 },
      (decodedText) => { if (!stopped) handleScannedCard(decodedText); },
      () => {} // per-frame "no code found" — ignore
    ).then(() => { started = true; })
     .catch(() => alert('Could not access the camera. Check camera permissions and try again.'));

    return () => {
      stopped = true;
      if (!started) return;
      try { scanner.stop().then(() => scanner.clear()).catch(() => {}); } catch (e) {}
    };
  }, [showScanner]);

  async function handleScannedCard(decodedText) {
    if (scanBusy) return;
    setScanBusy(true);
    // Cards now encode a real URL (https://.../c/<mid>) so any camera app can
    // open them — pull the mid out of the path instead of a bare "WWMID:" prefix.
    const match = /\/c\/([^/?#]+)/.exec(decodedText.trim());
    const mid = match ? match[1] : decodedText.trim();
    try {
      const loc = await getCurrentLocation();
      const res = await axios.post('/api/orders/deliver-by-mid', { mid, ...(loc || {}) });
      setShowScanner(false);
      stopTimer(res.data.oid);
      const delivered = orders.find(o => o.oid === res.data.oid);
      if (delivered) setSessionDeliveries(prev => [delivered, ...prev]);
      setOrders(prev => prev.filter(o => o.oid !== res.data.oid));
      fetchEarnings();
      fetchHistory();
      alert('✅ Delivery confirmed via card scan!');
    } catch(e) {
      alert(e.response?.data?.error || 'Scan failed');
    } finally {
      setScanBusy(false);
    }
  }

  // Fallback for when the camera isn't working — the customer reads their
  // dashboard code aloud instead of the driver scanning their card.
  async function submitDeliveryCode() {
    if (codeBusy || !codeInput.trim()) return;
    setCodeBusy(true);
    try {
      const loc = await getCurrentLocation();
      const res = await axios.post('/api/orders/deliver-by-code', { code: codeInput.trim(), ...(loc || {}) });
      setShowCodeEntry(false);
      setCodeInput('');
      stopTimer(res.data.oid);
      const delivered = orders.find(o => o.oid === res.data.oid);
      if (delivered) setSessionDeliveries(prev => [delivered, ...prev]);
      setOrders(prev => prev.filter(o => o.oid !== res.data.oid));
      fetchEarnings();
      fetchHistory();
      alert('✅ Delivery confirmed via code!');
    } catch(e) {
      alert(e.response?.data?.error || 'Invalid code');
    } finally {
      setCodeBusy(false);
    }
  }

  async function markAttempted(oid) {
    setAttempting(oid);
    try {
      await axios.put(`/api/orders/${oid}/attempted`);
      stopTimer(oid);
      const attempted = orders.find(o => o.oid === oid);
      if (attempted) setSessionDeliveries(prev => [{ ...attempted, status: 'Attempted Delivery' }, ...prev]);
      setOrders(prev => prev.filter(o => o.oid !== oid));
      fetchEarnings();
      fetchHistory();
    } catch(e) { alert(e.response?.data?.error || 'Failed'); }
    finally { setAttempting(null); }
  }

  async function needHelp(oid) {
    try {
      await axios.post(`/api/orders/${oid}/need-help`, { driverName: user?.name });
      alert('✅ Dispatcher notified. They will try to reach the customer.');
    } catch(e) { alert('Failed to alert dispatcher'); }
  }

  // Remembered per-device, not per-account — a driver's phone is their
  // own, and this is exactly the kind of preference that shouldn't need
  // a server round-trip or show up anywhere else in the app.
  const NAV_APP_KEY = 'ww_driver_nav_app';
  function getPreferredNavApp() {
    try { return localStorage.getItem(NAV_APP_KEY) || 'google'; } catch(e) { return 'google'; }
  }
  function setPreferredNavApp(app) {
    try { localStorage.setItem(NAV_APP_KEY, app); } catch(e) {}
  }

  const NAV_APPS = {
    google: { label: 'Google Maps', buildUrl: (address) => `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}` },
    waze:   { label: 'Waze',        buildUrl: (address) => `https://waze.com/ul?q=${encodeURIComponent(address)}&navigate=yes` },
    apple:  { label: 'Apple Maps',  buildUrl: (address) => `https://maps.apple.com/?daddr=${encodeURIComponent(address)}` },
  };

  // Opening the app's own universal link (rather than a plain https://
  // domain) is what lets the OS hand off to the installed app when it's
  // present — the same link just opens that service's own website when
  // it isn't, which is the "appropriate fallback" with zero extra work.
  function navigate(address, app) {
    const useApp = app || getPreferredNavApp();
    if (app) setPreferredNavApp(app);
    window.open(NAV_APPS[useApp].buildUrl(address), '_blank');
  }

  // Format timer display
  function formatTimer(seconds) {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  }

  function getTimerColor(seconds) {
    if (seconds >= 600) return 'var(--accent-red)'; // 10+ minutes - red
    if (seconds >= 240) return '#f0a020';            // 4+ minutes - orange
    return 'var(--accent-green)';                    // under 4 min - green
  }

  const deliveredToday = sessionDeliveries.filter(o => o.status !== 'Attempted Delivery').length;

  return (
    <div className="app-shell">
      {/* Header */}
      <div style={{ padding:'18px 20px 14px', borderBottom:'1px solid var(--border)', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
        <div>
          <p style={{ fontSize:11, color:'var(--text-secondary)', marginBottom:2 }}>Welcome back,</p>
          <h2 style={{ fontFamily:'Bebas Neue, sans-serif', fontSize:20, letterSpacing:2 }}>{user?.name} 👋</h2>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          <button onClick={() => setShowDispatchChat(true)}
            title="Message dispatch"
            style={{ position:'relative', background:'#0a1530', color:'var(--accent-blue)', border:'1px solid var(--border-hover)', borderRadius:20, fontSize:14, padding:'6px 10px', cursor:'pointer' }}>
            💬
            {unread.directThread?.unreadCount > 0 && (
              <span style={{ position:'absolute', top:-5, right:-5, background:'#c0101a', color:'#fff', borderRadius:'50%', width:16, height:16, display:'flex', alignItems:'center', justifyContent:'center', fontSize:9, fontFamily:'DM Sans,sans-serif' }}>
                {unread.directThread.unreadCount}
              </span>
            )}
          </button>
          <button onClick={() => { setLoading(true); fetchOrders(); fetchEarnings(); }}
            title="Refresh deliveries"
            style={{ background:'#0a1530', color:'var(--accent-blue)', border:'1px solid var(--border-hover)', borderRadius:20, fontSize:14, padding:'6px 10px', cursor:'pointer' }}>
            🔄
          </button>
          <button onClick={telegram.openOrReopen}
            title={telegram.linked ? 'Open Telegram' : 'Connect Telegram'}
            style={{ background:'#0a1530', color:'var(--accent-blue)', border:'1px solid var(--border-hover)', borderRadius:20, fontSize:14, padding:'6px 10px', cursor:'pointer' }}>
            ✈️
          </button>
          <div style={{ background:'#0a1a10', color:'var(--accent-green)', border:'1px solid #0a3020', fontSize:11, padding:'4px 12px', borderRadius:20 }}>● Online</div>
        </div>
      </div>

      {/* Stats */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr 1fr', gap:8, padding:'12px 20px' }}>
        {[
          { label:'Active',   val:orders.length,              color:'var(--accent-blue)'  },
          { label:'Today',    val:deliveredToday,             color:'var(--accent-blue)'  },
          { label:'Earned',   val:`${earnings.today}c`,       color:'var(--accent-green)' },
          { label:'Balance',  val:`${earnings.balance}c`,     color:'var(--accent-green)' },
        ].map(s => (
          <div key={s.label} style={{ background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:12, padding:'10px 8px', textAlign:'center' }}>
            <p style={{ fontFamily:'Bebas Neue, sans-serif', fontSize:18, color:s.color, letterSpacing:1 }}>{s.val}</p>
            <p style={{ fontSize:9, color:'var(--text-secondary)', textTransform:'uppercase', letterSpacing:1, marginTop:2 }}>{s.label}</p>
          </div>
        ))}
      </div>

      <InstallPrompt />

      {/* Telegram opt-in */}
      {telegram.linked === false && !telegram.dismissed && (
        <div style={{
          margin:'0 20px 14px', background:'var(--bg-card)',
          border:'1px solid var(--border)', borderRadius:'var(--radius-lg)',
          padding:'12px 14px', position:'relative'
        }}>
          <button onClick={telegram.dismiss} aria-label="Dismiss"
            style={{ position:'absolute', top:10, right:10, background:'none', border:'none', color:'var(--text-muted)', fontSize:16, cursor:'pointer', lineHeight:1 }}>
            ✕
          </button>
          <p style={{ fontSize:13, color:'var(--text-primary)', marginBottom:4, paddingRight:20 }}>✈️ Connect Telegram</p>
          <p style={{ fontSize:11, color:'var(--text-secondary)', marginBottom:10 }}>
            Get new-order and payout alerts as Telegram messages, even when the app's closed.
          </p>
          {telegram.code && (
            <p style={{ fontSize:11, color:'var(--text-secondary)', marginBottom:10 }}>
              Open Telegram and send: <strong>/start {telegram.code}</strong>
            </p>
          )}
          <button onClick={telegram.connect} disabled={telegram.connecting} style={{
            padding:'8px 16px', borderRadius:'var(--radius)', border:'none',
            background:'#229ED9', color:'#fff', fontSize:13, cursor:'pointer'
          }}>
            {telegram.connecting ? 'Waiting for you to connect…' : 'Connect Telegram'}
          </button>
        </div>
      )}
      {telegram.linked === true && (
        <p style={{ margin:'0 20px 10px', fontSize:11, color:'var(--text-muted)' }}>✈️ Telegram connected</p>
      )}

      {/* Content */}
      <div style={{ flex:1, overflowY:'auto', padding:'0 20px' }}>

        {tab === 'queue' && (
          <>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', paddingBottom:10 }}>
              <p style={{ fontFamily:'Bebas Neue, sans-serif', fontSize:16, letterSpacing:1 }}>My Deliveries</p>
              <p style={{ fontSize:11, color:'var(--text-secondary)' }}>{orders.length} active</p>
            </div>

            {orders.length > 0 && (
              <div style={{ display:'flex', gap:8, marginBottom:14 }}>
                <button onClick={()=>setShowScanner(true)} style={{ flex:1, padding:12, background:'#0a1530', color:'var(--accent-blue)', border:'1px solid var(--border-hover)', borderRadius:12, fontSize:13, cursor:'pointer', fontFamily:'DM Sans,sans-serif', fontWeight:500 }}>
                  📷 Scan Member Card
                </button>
                <button onClick={()=>setShowCodeEntry(true)} style={{ flex:1, padding:12, background:'#0a1530', color:'var(--accent-blue)', border:'1px solid var(--border-hover)', borderRadius:12, fontSize:13, cursor:'pointer', fontFamily:'DM Sans,sans-serif', fontWeight:500 }}>
                  ⌨️ Enter Code Instead
                </button>
              </div>
            )}

            {loading ? (
              <div className="loading">Loading orders...</div>
            ) : orders.length === 0 ? (
              <div style={{ textAlign:'center', marginTop:60 }}>
                <div style={{ fontSize:40, marginBottom:12 }}>✅</div>
                <p style={{ color:'var(--text-secondary)', fontSize:14 }}>No active deliveries.</p>
              </div>
            ) : (
              orders.map((order, i) => {
                const timerSecs  = timers[order.oid] || 0;
                const isAtStop   = order.status === 'Driver Arrived';
                const showHelp   = isAtStop && timerSecs >= 240;  // 4+ minutes
                const showAttempt = isAtStop && timerSecs >= 600; // 10+ minutes

                // Available whether or not the driver has already tapped
                // "I'm Here" — still useful after marking at-stop (lost
                // signal, re-parking, multiple close-together stops, etc.),
                // so both branches below render this instead of just the
                // pre-arrival one.
                const navigateButton = (
                  <div style={{ flex:1, position:'relative', display:'flex' }}>
                    <button onClick={() => navigate(order.address)}
                      style={{ flex:1, padding:10, background:'#0a1530', color:'var(--accent-blue)', border:'1px solid var(--border-hover)', borderRight:'none', borderRadius:'10px 0 0 10px', fontSize:12, cursor:'pointer', fontFamily:'DM Sans,sans-serif' }}>
                      🚗 Navigate
                    </button>
                    <button onClick={() => setNavPickerOid(navPickerOid === order.oid ? null : order.oid)}
                      title="Choose navigation app"
                      style={{ padding:'10px 10px', background:'#0a1530', color:'var(--accent-blue)', border:'1px solid var(--border-hover)', borderRadius:'0 10px 10px 0', fontSize:12, cursor:'pointer', fontFamily:'DM Sans,sans-serif' }}>
                      ▾
                    </button>
                    {navPickerOid === order.oid && (
                      <div style={{ position:'absolute', top:'calc(100% + 4px)', left:0, zIndex:10, background:'var(--bg-card)', border:'1px solid var(--border-hover)', borderRadius:10, overflow:'hidden', minWidth:160, boxShadow:'0 4px 16px rgba(0,0,0,0.4)' }}>
                        {Object.entries(NAV_APPS).map(([key, app], i) => (
                          <button key={key} onClick={() => { navigate(order.address, key); setNavPickerOid(null); }}
                            style={{
                              display:'flex', alignItems:'center', justifyContent:'space-between', width:'100%', padding:'10px 12px',
                              background: getPreferredNavApp() === key ? 'var(--bg-base)' : 'transparent', color:'var(--text-primary)',
                              border:'none', borderBottom: i < Object.keys(NAV_APPS).length - 1 ? '1px solid var(--border)' : 'none',
                              fontSize:12, textAlign:'left', cursor:'pointer', fontFamily:'DM Sans,sans-serif'
                            }}>
                            {app.label}
                            {getPreferredNavApp() === key && <span style={{ color:'var(--accent-blue)' }}>✓</span>}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );

                return (
                  <div key={order.oid} style={{ background:'var(--bg-card)', border:`1px solid ${i === 0 ? '#c0101a50' : 'var(--border)'}`, borderRadius:'var(--radius-lg)', marginBottom:12, padding:16 }}>

                    {/* Top row */}
                    <div style={{ display:'flex', justifyContent:'space-between', marginBottom:12, paddingBottom:10, borderBottom:'1px solid var(--border)' }}>
                      <div>
                        <p style={{ fontFamily:'Bebas Neue, sans-serif', fontSize:18, color:'var(--accent-blue)', letterSpacing:1 }}>{order.oid}</p>
                        <p style={{ fontSize:11, color:'var(--text-secondary)', marginTop:1 }}>{order.time}</p>
                      </div>
                      <div style={{ textAlign:'right' }}>
                        <p style={{ fontFamily:'Bebas Neue, sans-serif', fontSize:22, color:'var(--accent-green)', letterSpacing:1 }}>{order.total}</p>
                        {i === 0 && <span style={{ background:'#1a0505', color:'var(--accent-red)', border:'1px solid #3a0a0a', borderRadius:20, fontSize:10, padding:'2px 8px', letterSpacing:1, textTransform:'uppercase' }}>Priority</span>}
                      </div>
                    </div>

                    {/* Timer display when at stop */}
                    {isAtStop && (
                      <div style={{ background:'var(--bg-base)', borderRadius:10, padding:'8px 12px', marginBottom:10, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                        <p style={{ fontSize:12, color:'var(--text-secondary)' }}>Time at stop</p>
                        <p style={{ fontFamily:'Bebas Neue, sans-serif', fontSize:18, color:getTimerColor(timerSecs), letterSpacing:1 }}>
                          {formatTimer(timerSecs)}
                        </p>
                      </div>
                    )}

                    {/* Customer info */}
                    <div style={{ display:'flex', flexDirection:'column', gap:5, marginBottom:10 }}>
                      <div style={{ display:'flex', gap:8, alignItems:'flex-start' }}>
                        <span style={{ fontSize:13, width:16, flexShrink:0 }}>👤</span>
                        <span style={{ fontSize:13, color:'var(--text-primary)' }}>{order.name}</span>
                      </div>
                      <div style={{ display:'flex', gap:8, alignItems:'flex-start' }}>
                        <span style={{ fontSize:13, width:16, flexShrink:0 }}>📍</span>
                        <span style={{ fontSize:13, color:'var(--text-primary)' }}>{order.address}</span>
                      </div>
                    </div>

                    {/* Shown by default right under the address, not buried
                        below the action buttons where it needed a scroll. */}
                    {!hiddenMaps.has(order.oid) && (
                      <div style={{ marginBottom:10 }}>
                        <DeliveryMiniMap oid={order.oid} address={order.address} myLocation={myLocation} />
                      </div>
                    )}

                    {/* Call needs the masked relay number; messaging doesn't — it works
                        in-app regardless of whether the voice relay set up successfully. */}
                    <div style={{ display:'flex', gap:8, marginBottom:10 }}>
                      {order.relayNumber && (
                        <a href={`tel:${order.relayNumber}`}
                          style={{ flex:1, textAlign:'center', padding:9, background:'#0a1530', color:'var(--accent-blue)', border:'1px solid var(--border-hover)', borderRadius:10, fontSize:12, cursor:'pointer', fontFamily:'DM Sans,sans-serif', textDecoration:'none' }}>
                          📞 Call
                        </a>
                      )}
                      <button onClick={() => setChatOid(order.oid)}
                        style={{ position:'relative', flex:1, textAlign:'center', padding:9, background:'#0a1530', color:'var(--accent-blue)', border:'1px solid var(--border-hover)', borderRadius:10, fontSize:12, cursor:'pointer', fontFamily:'DM Sans,sans-serif' }}>
                        💬 Message
                        {unread.orderThreads[order.oid] > 0 && (
                          <span style={{ position:'absolute', top:-6, right:-6, background:'#c0101a', color:'#fff', borderRadius:'50%', width:17, height:17, display:'flex', alignItems:'center', justifyContent:'center', fontSize:9, fontFamily:'DM Sans,sans-serif' }}>
                            {unread.orderThreads[order.oid]}
                          </span>
                        )}
                      </button>
                    </div>
                    {!order.relayNumber && (
                      <p style={{ fontSize:11, color:'var(--text-secondary)', marginBottom:10 }}>Setting up a private call number for this delivery…</p>
                    )}
                    {showHelp && !showAttempt && (
                      <div style={{ display:'flex', gap:8, marginBottom:10 }}>
                        <button onClick={() => needHelp(order.oid)}
                          style={{ flex:1, padding:9, background:'#1a0800', color:'#f0a020', border:'1px solid #3a2000', borderRadius:10, fontSize:12, cursor:'pointer', fontFamily:'DM Sans,sans-serif', fontWeight:500 }}>
                          🆘 Need Help
                        </button>
                      </div>
                    )}

                    {/* Order summary */}
                    <div style={{ background:'var(--bg-base)', borderRadius:10, padding:'10px 12px', marginBottom:12 }}>
                      <p style={{ fontSize:10, color:'var(--text-secondary)', textTransform:'uppercase', letterSpacing:1, marginBottom:4 }}>Order Summary</p>
                      <p style={{ fontSize:13, color:'#888' }}>{order.summary}</p>
                    </div>

                    {/* Action buttons */}
                    {!isAtStop ? (
                      <div style={{ display:'flex', gap:8 }}>
                        {navigateButton}
                        <button onClick={() => toggleMap(order.oid)}
                          style={{ flex:1, padding:10, background:'#0a1530', color:'var(--accent-blue)', border:'1px solid var(--border-hover)', borderRadius:10, fontSize:12, cursor:'pointer', fontFamily:'DM Sans,sans-serif' }}>
                          🗺️ {hiddenMaps.has(order.oid) ? 'Map' : 'Hide Map'}
                        </button>
                        <button onClick={() => markArrived(order.oid)}
                          style={{ flex:1, padding:10, background:'#0a2010', color:'var(--accent-green)', border:'1px solid #0a3020', borderRadius:10, fontSize:12, cursor:'pointer', fontFamily:'DM Sans,sans-serif', fontWeight:500 }}>
                          📍 I'm Here
                        </button>
                      </div>
                    ) : (
                      <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                        <div style={{ display:'flex', gap:8 }}>
                          {navigateButton}
                          <button onClick={() => toggleMap(order.oid)}
                            style={{ flex:1, padding:10, background:'#0a1530', color:'var(--accent-blue)', border:'1px solid var(--border-hover)', borderRadius:10, fontSize:12, cursor:'pointer', fontFamily:'DM Sans,sans-serif' }}>
                            🗺️ {hiddenMaps.has(order.oid) ? 'Map' : 'Hide Map'}
                          </button>
                        </div>
                        <div style={{ display:'flex', gap:8 }}>
                          {showAttempt && (
                            <button onClick={() => markAttempted(order.oid)} disabled={attempting === order.oid}
                              style={{ flex:1, padding:10, background:'#1a0505', color:'var(--accent-red)', border:'1px solid #3a0a0a', borderRadius:10, fontSize:12, cursor:'pointer', fontFamily:'DM Sans,sans-serif', fontWeight:500 }}>
                              {attempting === order.oid ? 'Processing...' : '❌ Attempted'}
                            </button>
                          )}
                          <button onClick={() => markDelivered(order.oid)} disabled={delivering === order.oid}
                            style={{ flex:1, padding:10, background:'var(--accent-red)', color:'#fff', border:'none', borderRadius:10, fontSize:13, cursor:'pointer', fontFamily:'DM Sans,sans-serif', fontWeight:500 }}>
                            {delivering === order.oid ? 'Confirming...' : '✅ Mark Delivered'}
                          </button>
                        </div>
                      </div>
                    )}

                  </div>
                );
              })
            )}
          </>
        )}

        {tab === 'history' && (
          <>
            <p style={{ fontFamily:'Bebas Neue, sans-serif', fontSize:16, letterSpacing:1, paddingBottom:10 }}>Recent Deliveries</p>
            {history.length === 0 ? (
              <p style={{ color:'var(--text-secondary)', fontSize:13 }}>No deliveries yet.</p>
            ) : (
              history.map((order, i) => order && (
                <div key={`${order.oid}-${i}`} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'12px 0', borderBottom:'1px solid var(--bg-card)' }}>
                  <div>
                    <p style={{ fontFamily:'Bebas Neue, sans-serif', fontSize:14, color:'var(--text-secondary)', letterSpacing:1 }}>{order.oid}</p>
                    <p style={{ fontSize:13, color:'var(--text-primary)' }}>{order.name}</p>
                    <p style={{ fontSize:11, color:'var(--text-secondary)', marginTop:2 }}>{order.address}</p>
                  </div>
                  <div style={{ textAlign:'right' }}>
                    <p style={{ fontFamily:'Bebas Neue, sans-serif', fontSize:16, color: order.status === 'Attempted Delivery' ? 'var(--accent-red)' : 'var(--accent-green)' }}>
                      {order.total}
                    </p>
                    <p style={{ fontSize:10, color: order.status === 'Attempted Delivery' ? 'var(--accent-red)' : 'var(--text-secondary)', marginTop:2 }}>
                      {order.status === 'Attempted Delivery' ? 'Attempted' : 'Delivered'}
                    </p>
                  </div>
                </div>
              ))
            )}
          </>
        )}

        {tab === 'earnings' && (
          <>
            <p style={{ fontFamily:'Bebas Neue, sans-serif', fontSize:16, letterSpacing:1, paddingBottom:10 }}>My Earnings</p>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:20 }}>
              {[
                { label:'Today',        val:`${earnings.today} coins`,   color:'var(--accent-blue)'  },
                { label:'This Week',    val:`${earnings.week} coins`,    color:'var(--accent-blue)'  },
                { label:'Balance',      val:`${earnings.balance} coins`, color:'var(--accent-green)' },
                { label:'Deliveries',   val:deliveredToday,              color:'var(--text-primary)' },
              ].map(e => (
                <div key={e.label} style={{ background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:14, padding:16 }}>
                  <p style={{ fontSize:11, color:'var(--text-secondary)', textTransform:'uppercase', letterSpacing:1, marginBottom:6 }}>{e.label}</p>
                  <p style={{ fontFamily:'Bebas Neue, sans-serif', fontSize:22, color:e.color, letterSpacing:1 }}>{e.val}</p>
                </div>
              ))}
            </div>
            {earnings.balance > 0 && (
              <button className="btn-primary" onClick={async () => {
                if (window.confirm(`Request payout of ${earnings.balance} coins?`)) {
                  try {
                    await axios.post('/api/drivers/cashout-request');
                    alert('✅ Payout request sent to admin!');
                    fetchEarnings();
                  } catch(e) { alert(e.response?.data?.error || 'Failed'); }
                }
              }}>
                Request Payout ({earnings.balance} coins)
              </button>
            )}
          </>
        )}

        {tab === 'promos' && (
          <>
            <p style={{ fontFamily:'Bebas Neue, sans-serif', fontSize:16, letterSpacing:1, paddingBottom:10 }}>Promos</p>
            {promos.length === 0 ? (
              <p style={{ fontSize:13, color:'var(--text-secondary)' }}>Nothing posted right now — check back later.</p>
            ) : (
              promos.map(p => {
                const goal = p.goalType === 'driver_deliveries' ? promoProgress.find(pr => pr.promo.id === p.id) : null;
                const pct = goal ? Math.min(100, Math.round((goal.count / p.goalTarget) * 100)) : 0;
                return (
                  <div key={p.id} className="card" style={{ marginBottom:12 }}>
                    <p style={{ fontFamily:'Bebas Neue, sans-serif', fontSize:16, letterSpacing:1, color:'var(--accent-gold)', marginBottom:6 }}>{p.title}</p>
                    {p.description && <p style={{ fontSize:13, color:'var(--text-primary)', marginBottom:8, lineHeight:1.5 }}>{p.description}</p>}
                    {goal && (
                      <div style={{ marginTop:8, marginBottom:8 }}>
                        <div style={{ display:'flex', justifyContent:'space-between', marginBottom:4 }}>
                          <span style={{ fontSize:11, color:'var(--text-secondary)' }}>{goal.count} of {p.goalTarget} deliveries</span>
                          <span style={{ fontSize:11, color:'var(--accent-green)' }}>+{p.goalBonusCoins}c on completion</span>
                        </div>
                        <div style={{ background:'var(--bg-input)', borderRadius:20, height:8, overflow:'hidden' }}>
                          <div style={{ width:`${pct}%`, height:'100%', background: pct >= 100 ? 'var(--accent-green)' : 'var(--accent-blue)', borderRadius:20 }} />
                        </div>
                      </div>
                    )}
                    {p.endDate && <p style={{ fontSize:11, color:'var(--text-secondary)' }}>Through {p.endDate}</p>}
                  </div>
                );
              })
            )}
          </>
        )}
      </div>

      {/* QR Scanner Modal */}
      {showScanner && (
        <div style={{ position:'fixed', inset:0, background:'#000000ee', display:'flex', alignItems:'center', justifyContent:'center', zIndex:2000, flexDirection:'column', padding:20 }}>
          <p style={{ fontFamily:'Bebas Neue,sans-serif', fontSize:20, letterSpacing:2, color:'#fff', marginBottom:16 }}>Scan Member Card</p>
          <div id="qr-reader" style={{ width:'100%', maxWidth:320, borderRadius:14, overflow:'hidden' }} />
          {scanBusy && <p style={{ color:'#fff', fontSize:13, marginTop:14 }}>Confirming delivery...</p>}
          <button onClick={()=>setShowScanner(false)} style={{ marginTop:20, background:'none', border:'1px solid #fff', color:'#fff', borderRadius:20, padding:'10px 24px', fontSize:13, cursor:'pointer', fontFamily:'DM Sans,sans-serif' }}>
            Cancel
          </button>
        </div>
      )}

      {/* Delivery Code Entry Modal */}
      {showCodeEntry && (
        <div style={{ position:'fixed', inset:0, background:'#000000ee', display:'flex', alignItems:'center', justifyContent:'center', zIndex:2000, flexDirection:'column', padding:20 }}>
          <p style={{ fontFamily:'Bebas Neue,sans-serif', fontSize:20, letterSpacing:2, color:'#fff', marginBottom:16 }}>Enter Delivery Code</p>
          <p style={{ color:'var(--text-secondary)', fontSize:12, marginBottom:16, textAlign:'center', maxWidth:280 }}>
            Ask the customer for the code shown on their dashboard
          </p>
          <input
            type="tel"
            inputMode="numeric"
            autoFocus
            value={codeInput}
            onChange={e => setCodeInput(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder="000000"
            style={{ width:'100%', maxWidth:240, padding:'14px 0', textAlign:'center', fontFamily:'Bebas Neue,sans-serif', fontSize:28, letterSpacing:8, background:'var(--bg-base)', border:'1px solid var(--border-hover)', borderRadius:12, color:'#fff' }}
          />
          {codeBusy && <p style={{ color:'#fff', fontSize:13, marginTop:14 }}>Confirming delivery...</p>}
          <button onClick={submitDeliveryCode} disabled={codeBusy || codeInput.length !== 6} style={{ marginTop:20, background:'var(--accent-blue)', border:'none', color:'#fff', borderRadius:20, padding:'10px 24px', fontSize:13, cursor:'pointer', fontFamily:'DM Sans,sans-serif', opacity: codeInput.length !== 6 ? 0.5 : 1 }}>
            Confirm Delivery
          </button>
          <button onClick={()=>{ setShowCodeEntry(false); setCodeInput(''); }} style={{ marginTop:10, background:'none', border:'1px solid #fff', color:'#fff', borderRadius:20, padding:'10px 24px', fontSize:13, cursor:'pointer', fontFamily:'DM Sans,sans-serif' }}>
            Cancel
          </button>
        </div>
      )}

      {/* Nav */}
      <div className="nav-bar">
        <button className={`nav-item ${tab === 'queue' ? 'active' : ''}`} onClick={() => setTab('queue')}><span style={{ fontSize:17 }}>📌</span>Queue</button>
        <button className={`nav-item ${tab === 'history' ? 'active' : ''}`} onClick={() => setTab('history')}><span style={{ fontSize:17 }}>📋</span>History</button>
        <button className={`nav-item ${tab === 'earnings' ? 'active' : ''}`} onClick={() => setTab('earnings')}><span style={{ fontSize:17 }}>💰</span>Earnings</button>
        <button className={`nav-item ${tab === 'promos' ? 'active' : ''}`} onClick={() => setTab('promos')}><span style={{ fontSize:17 }}>📢</span>Promos</button>
        <button className="nav-item" onClick={logout}><span style={{ fontSize:17 }}>↩</span>Out</button>
      </div>

      {chatOid && (
        <ChatThread
          title="Message customer"
          selfRole="driver"
          fetchUrl={`/api/orders/${chatOid}/messages`}
          postUrl={`/api/orders/${chatOid}/messages`}
          onClose={() => { setChatOid(null); fetchUnread(); }}
        />
      )}

      {showDispatchChat && (
        <ChatThread
          title="Message Dispatch"
          selfRole="driver"
          fetchUrl={`/api/messages/thread/driver/${encodeURIComponent(user?.name)}`}
          postUrl={`/api/messages/thread/driver/${encodeURIComponent(user?.name)}`}
          onClose={() => { setShowDispatchChat(false); fetchUnread(); }}
        />
      )}
    </div>
  );
}
